import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ClientQualificationDTO,
  FranchiseDetails,
  FranchiseDialogType,
  FranchiseSmsPriceDTO,
  FranchiseWithStoresDTO,
} from '../type';
import franchisesAdapter from './franchises.adapter';
import addApiKeyReducers from './franchisesApiKey.reducers';

export interface FranchiseState {
  id: string;
  isLoading: boolean;
  entity: FranchiseDetails;
  ids: string[];
  entities: { [key: string]: FranchiseWithStoresDTO };
  franchiseDialog: {
    type: FranchiseDialogType | undefined;
    isOpen: boolean;
    isLoading: boolean;
  };

  qualificationModal: {
    isOpen: boolean;
  };

  averageBasketModal: {
    isOpened: boolean;
  };

  apiKey: {
    v1: { key?: string; isLoading: boolean; hasError: boolean };
    v2: { key?: string; isLoading: boolean; hasError: boolean };
    dailyapps: { key?: string; isLoading: boolean; hasError: boolean };
  };

  smsPrice: FranchiseSmsPriceDTO;
}

export const initialState: FranchiseState = {
  id: '',
  ids: [],
  entities: {},
  entity: {
    id: '',
    logo: '',
    name: '',
    dateCreated: '',
    lastModified: '',
    currencyCode: '',
    isOfficial: false,
    description: '',
    timezone: '',
    averageBasket: 0,
    userId: '',
    storeListId: '',
    codeAlpha3: '',
    senderId: '',
    theme: {
      primaryColor: '',
      secondaryColor: '',
      tertiaryColor: '',
    },
    features: [],
  },
  apiKey: {
    v1: { isLoading: false, hasError: false },
    v2: { isLoading: false, hasError: false },
    dailyapps: { isLoading: false, hasError: false },
  },
  isLoading: true,
  franchiseDialog: {
    type: undefined,
    isOpen: false,
    isLoading: false,
  },
  qualificationModal: {
    isOpen: false,
  },
  averageBasketModal: {
    isOpened: false,
  },
  smsPrice: {
    isAvailable: true,
    unitPrice: null,
  },
};

export const { actions, reducer } = createSlice({
  name: 'Franchise',
  initialState: franchisesAdapter.getInitialState(initialState),
  reducers: {
    openNewFranchiseDialog: (state: FranchiseState, action: PayloadAction<FranchiseDialogType | undefined>) => {
      state.franchiseDialog = {
        ...state.franchiseDialog,
        type: action.payload ?? FranchiseDialogType.NEW,
        isOpen: true,
        isLoading: false,
      };
    },
    closeFranchiseDialog: (state: FranchiseState, action: PayloadAction<FranchiseDialogType | undefined>) => {
      state.franchiseDialog = {
        ...state.franchiseDialog,
        type: action.payload ?? FranchiseDialogType.NEW,
        isOpen: false,
      };
    },
    setFranchiseDialogIsLoading: (state: FranchiseState, action) => {
      state.franchiseDialog = {
        ...state.franchiseDialog,
        isLoading: action.payload,
      };
    },
    getFranchisesSuccess: (state, action: PayloadAction<FranchiseWithStoresDTO[]>) => {
      franchisesAdapter.setAll(state, action.payload);
    },
    getFranchisesFailed: state => {
      franchisesAdapter.setAll(state, []);
    },
    updateUserFranchise: (state, action: PayloadAction<Partial<FranchiseDetails>>) => {
      state.entity = { ...state.entity, ...action.payload };
    },
    updateQualificationClientFranchise: (state, action: PayloadAction<ClientQualificationDTO>) => {
      state.entity.customersQualification = action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setIsModalOpen: (state, action) => {
      state.qualificationModal.isOpen = action.payload;
    },
    setIsAverageBasketModalOpened: (state, action) => {
      state.averageBasketModal.isOpened = action.payload;
    },
    setFranchiseSmsPrice: (state, action: PayloadAction<FranchiseSmsPriceDTO>) => {
      state.smsPrice = action.payload;
    },
    resetFranchiseSlice: () => initialState,
  },
  extraReducers: addApiKeyReducers,
});

export const { setIsModalOpen, setIsAverageBasketModalOpened, setFranchiseSmsPrice, resetFranchiseSlice } = actions;
